import { useEffect, useMemo } from 'react';

import type { Account } from '~/shared/utils/types';

import AutoScalingStateFilter from '../AutoScalingStateFilter';
import AutoscalingEligibilityFilter from '../AutoscalingEligibilityFilter';
import CapacityRangeFilter from '../CapacityRangeFilter';
import FilterSection from '../FilterSection';
import { formatCapacityRangeLabel } from '../capacityFormat';
import { useDisclosureOpenState } from '../hooks';

import {
  volumeFilterSectionDefaultOpen,
  volumeFilterSections,
  type VolumeFilterSectionId,
} from './sections';
import { hasCapacityFilter, type VolumeFilterPanelState } from './panelState';

interface FilterPanelContentProps {
  panelOpen?: boolean;
  pending: VolumeFilterPanelState;
  setField: <K extends keyof VolumeFilterPanelState>(
    key: K
  ) => (value: VolumeFilterPanelState[K]) => void;
  /** Capacity has dedicated setters — see `useVolumeFilterState`. */
  setCapacityMin: (min: number) => void;
  setCapacityMax: (max: number) => void;
  clearCapacity: () => void;
  isOrgView?: boolean;
  accounts?: Account[];
}

export default function VolumeFilterPanelContent({
  panelOpen = false,
  pending,
  setField,
  setCapacityMin,
  setCapacityMax,
  clearCapacity,
  isOrgView = false,
  accounts = [],
}: FilterPanelContentProps) {
  const disclosures = useDisclosureOpenState<VolumeFilterSectionId>(volumeFilterSectionDefaultOpen);
  const { initializeOnce } = disclosures;

  const visibleSections = useMemo(
    () =>
      volumeFilterSections.filter(
        (s) => s.kind === 'capacity' || !s.orgViewOnly || (isOrgView && accounts.length > 0)
      ),
    [isOrgView, accounts.length]
  );

  /** Sections that already carry a selection start expanded, once per panel-open. */
  const sectionsWithSelection = useMemo(() => {
    const map: Partial<Record<VolumeFilterSectionId, boolean>> = {};
    visibleSections.forEach((s) => {
      map[s.id] =
        s.kind === 'capacity' ? hasCapacityFilter(pending) : pending[s.stateKey].length > 0;
    });
    return map;
  }, [pending, visibleSections]);

  useEffect(() => {
    initializeOnce(panelOpen, sectionsWithSelection);
  }, [panelOpen, sectionsWithSelection, initializeOnce]);

  const accountsScope = useMemo(() => {
    if (!isOrgView) return undefined;
    return pending.accountFilters.length > 0
      ? pending.accountFilters.map((f) => f.id).join(',')
      : 'all';
  }, [isOrgView, pending.accountFilters]);

  return (
    <div className="flex-1 overflow-y-auto pb-4">
      <AutoScalingStateFilter
        pendingAutoscaleState={pending.autoscaleState}
        setPendingAutoscaleState={setField('autoscaleState')}
      />
      <AutoscalingEligibilityFilter
        pendingEligibility={pending.eligibility}
        setPendingEligibility={setField('eligibility')}
      />
      {visibleSections.map((section) => {
        const open = disclosures.isOpen(section.id);

        if (section.kind === 'capacity') {
          return (
            <FilterSection
              key={section.id}
              title={section.title}
              open={open}
              onOpenChange={disclosures.setOpen(section.id)}
              // A range has no count — the shell renders this label in the badge's place.
              // Driven by `hasCapacityFilter` rather than by narrowedness, so a range seeded
              // from the URL onto widened bounds still shows its badge.
              badgeLabel={
                hasCapacityFilter(pending)
                  ? formatCapacityRangeLabel(pending.capacityMin, pending.capacityMax)
                  : undefined
              }
              onClear={clearCapacity}
            >
              <CapacityRangeFilter
                bounds={pending.capacityBounds}
                pendingCapacityMin={pending.capacityMin}
                pendingCapacityMax={pending.capacityMax}
                setPendingCapacityMin={setCapacityMin}
                setPendingCapacityMax={setCapacityMax}
                panelOpen={panelOpen}
              />
            </FilterSection>
          );
        }

        const filters = pending[section.stateKey];
        const setFilters = setField(section.stateKey);

        return (
          <FilterSection
            key={section.id}
            title={section.title}
            open={open}
            onOpenChange={disclosures.setOpen(section.id)}
            selectedCount={filters.length}
            onClear={() => setFilters([])}
          >
            {section.render({
              filters,
              setFilters: (next) => setFilters(next.filter((f) => section.accepts(f.group))),
              isDisclosureOpen: open,
              accountsScope,
              accounts,
            })}
          </FilterSection>
        );
      })}
    </div>
  );
}
